/**
 * Normalize the various representations of images stored in history.
 * - Already-parsed array → return as-is
 * - JSON string (e.g. '["url1","url2"]') → JSON.parse
 * - Postgres array literal (e.g. '{url1,url2}') → split & trim
 */
export function parseImages(img: any): string[] | undefined {
  if (Array.isArray(img)) return img;

  if (typeof img === "string") {
    // Try JSON-encoded first
    try {
      return JSON.parse(img);
    } catch {
      // Fallback: Postgres array literal
      const m = img.match(/^\\{(.+)\\}$/);
      if (m) {
        return m[1]
          .split(",")
          .map((s) => s.trim().replace(/^"(.*)"$/, "$1"));
      }
    }
  }
  return undefined;
}

/**
 * Resize image client-side to balance quality and request size.
 * Uses max 1600×1600 px and JPEG quality 0.9 which keeps visual
 * fidelity while preventing backend 413 errors.
 */
export const resizeImage = (
  file: File,
  maxWidth = 1600,
  maxHeight = 1600,
  quality = 0.9,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = ev => {
      if (!ev.target) return reject(new Error("Failed to read file"));
      img.src = ev.target.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);

    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas unsupported"));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          if (!blob) return reject(new Error("Failed to create blob"));
          const r2 = new FileReader();
          r2.onloadend = () => resolve(r2.result as string);
          r2.onerror = reject;
          r2.readAsDataURL(blob);
        },
        "image/jpeg",
        quality,
      );
    };
  });